"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader, ChevronDown, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-20 text-center space-y-6 animate-in fade-in duration-700">
        <div className="h-px w-24 bg-neutral-900 mb-4" />
        <h3 className="text-3xl font-serif text-neutral-900 tracking-tight">Inquiry Received</h3>
        <p className="text-neutral-600 max-w-md mx-auto leading-relaxed">
          {successMessage}
        </p>
        <div className="h-px w-24 bg-neutral-900 mt-4" />
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
      {}
      <div className="space-y-6">
        <div className="flex items-baseline justify-between border-b border-neutral-200 pb-2">
          <h3 className="text-sm font-medium uppercase tracking-widest text-neutral-500">01. Collector Details</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-neutral-300 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-neutral-900 placeholder:text-neutral-300 text-neutral-900 h-10" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-neutral-300 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-neutral-900 placeholder:text-neutral-300 text-neutral-900 h-10" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Phone Number <span className="normal-case text-neutral-400">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} className="border-0 border-b border-neutral-300 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-neutral-900 placeholder:text-neutral-300 text-neutral-900 h-10" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-baseline justify-between border-b border-neutral-200 pb-2">
          <h3 className="text-sm font-medium uppercase tracking-widest text-neutral-500">02. The Piece</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="print_title" rules={{
          required: "Print title is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Print Title / ID</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., “Midnight Street”" {...field} className="border-0 border-b border-neutral-300 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-neutral-900 placeholder:text-neutral-300 text-neutral-900 h-10" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="frame_size" rules={{
          required: "Frame size is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Preferred Frame Size</FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className="w-full h-10 border-0 border-b border-neutral-300 bg-transparent py-2 text-sm text-neutral-900 outline-none focus:border-neutral-900 disabled:cursor-not-allowed disabled:opacity-50 appearance-none rounded-none px-0 cursor-pointer" {...field} value={field.value || ""}>
                      <option value="" disabled className="text-neutral-400">Select size</option>
                      {["8×10 in", "11×14 in", "16×20 in", "20×30 in", "Custom"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <ChevronDown className="absolute right-0 top-3 h-4 w-4 text-neutral-400 pointer-events-none" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-baseline justify-between border-b border-neutral-200 pb-2">
          <h3 className="text-sm font-medium uppercase tracking-widest text-neutral-500">03. The Finish</h3>
        </div>
        
        <FormField control={control} name="frame_material" rules={{
        required: "Frame material is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Frame Material</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {["Wood – Natural", "Wood – Dark Stain", "Metal – Brushed Aluminum", "Metal – Black"].map((option, i) => <FormItem key={option} className="flex items-center space-x-3 space-y-0" data-index={i}>
                      <FormControl>
                        <RadioGroupItem value={option} className="border-neutral-400 text-neutral-900 focus-visible:ring-neutral-900" />
                      </FormControl>
                      <FormLabel className="font-normal text-neutral-700 cursor-pointer">
                        {option}
                      </FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="space-y-1 pt-4">
              <FormLabel className="text-xs uppercase tracking-wide text-neutral-500">Additional Details / Requests</FormLabel>
              <FormControl>
                <Textarea placeholder="Any special instructions, custom dimensions, or questions..." {...field} className="min-h-[100px] border-0 border-b border-neutral-300 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-neutral-900 placeholder:text-neutral-300 text-neutral-900 resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-50 border border-red-100 text-red-800 text-sm rounded-sm">
          {submitError}
        </div>}

      <div className="pt-6 flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-none px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </> : <>
              Secure the Frame
              <ArrowRight className="ml-2 h-4 w-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function SecureTheFrame() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="secure-the-frame" className="dark bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-neutral-800/20 via-background to-background pointer-events-none" />

      <div ref={ref} className={`max-w-4xl mx-auto relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <div className="bg-[#FDFDFB] text-neutral-900 p-8 md:p-16 shadow-2xl relative">
          {}
          <div className="absolute top-0 left-0 w-full h-1 bg-neutral-900/5" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-r border-b border-neutral-200 m-4" />
          <div className="absolute top-0 left-0 w-16 h-16 border-l border-t border-neutral-200 m-4" />

          <div className="mb-12 text-center">
            <span className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-2 block">
              Ref. FF-INQ-2024
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 tracking-tight mb-4">
              Secure the Frame
            </h2>
            <p className="text-neutral-500 max-w-lg mx-auto font-light">
              Complete the details below to initiate your commission. We will review your request and confirm availability within 24 hours.
            </p>
          </div>

          <FormProvider formId="secure_frame_inquire" sectionName="secure-the-frame" defaultValues={{
          name: "",
          email: "",
          phone: "",
          print_title: "",
          frame_size: "",
          frame_material: "Wood – Natural",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          print_title: {
            required: true
          },
          frame_size: {
            required: true
          },
          frame_material: {
            required: true
          }
        }} submitText="Secure the Frame" successMessage="Thank you. Your inquiry has been securely recorded. We will be in touch shortly to finalize the details.">
            <InquiryForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}